import React from 'react';
import dynamic from 'next/dynamic';
import { twMerge } from 'tailwind-merge';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { type MintReferral, useMintReferral } from '@context/mintReferral';
import type { MintComponentSongInfoFragmentDoc } from '@graphql/reactQuery';
import { type FragmentType, MintComponentFragmentDoc } from '@graphql/reactQuery';
import { getFragment, gql } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { exhaustiveCheck } from '@utils/helpers';
import { SocialProofBar } from './shared/SocialProofBar';
import { SongInfo } from './shared/SongInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment MintComponent on Release {
    id
    auctionContractType
    chainId
    samAddress
    ...MintComponentSongInfo
    ...RegularMintInfo
    ...BondingCurveMintData
    ...TiersMintComponent
    ...ReleaseUpdates
  }
`);

const MintComponentLoader = () => <div className="h-[190px] w-full animate-pulse bg-neutral200" />;

const EditionMintComponent = dynamic(() => import('./Edition').then(m => m.EditionMintComponent), {
  ssr: false,
  loading: () => <MintComponentLoader />,
});
const BondingCurve = dynamic(() => import('./BondingCurve').then(m => m.BondingCurve), {
  ssr: false,
  loading: () => <MintComponentLoader />,
});
const TiersMintComponent = dynamic(() => import('./Tiers').then(m => m.TiersMintComponent), {
  ssr: false,
  loading: () => <MintComponentLoader />,
});

export function MintComponent({
  releaseData,
  collectButtonSource,
  isInModal = false,
  referral,
  TEMPORARY_showCustomPrice,
}: {
  releaseData: FragmentType<MintComponentFragmentDoc>;
  collectButtonSource: CollectButtonSource;
  isInModal?: boolean;
  referral: MintReferral;
  /**
   * DO NOT USE THIS PROP UNLESS YOU KNOW WHAT YOU ARE DOING
   * This is a temporary prop to hide the price for the tiers mint component
   * and show a custom price for the coinbase quest
   * Since there we are showing users the Sound Fee and showing them "Free" would be confusing
   * This prop should be removed once we have a better solution
   */
  TEMPORARY_showCustomPrice?: string;
}) {
  const { setReferral } = useMintReferral();

  useDeepCompareEffectNoCheck(() => {
    setReferral(referral);
  }, [referral, setReferral]);

  const release = getFragment(MintComponentFragmentDoc, releaseData);
  const {
    data: { isFirstPhaseCompleted, totalMinted },
  } = useSubscribeRelease({
    release,
  });

  // Prevent showing mint component when it's missing critical auction information
  if (!release.auctionContractType) return null;

  switch (release.auctionContractType) {
    case 'ARTIST': {
      return (
        <MintComponentLayout release={release} isInModal={isInModal}>
          <EditionMintComponent
            releaseInfo={release}
            collectButtonSource={collectButtonSource}
            isInModal={isInModal}
          />
          {!isInModal && <SocialProofBar releaseId={release.id} totalMinted={totalMinted} />}
        </MintComponentLayout>
      );
    }
    case 'EDITION': {
      const isSamEnabled = !!release.samAddress;
      const shouldShowBondingCurve =
        release.auctionContractType === 'EDITION' && isFirstPhaseCompleted && isSamEnabled;

      if (shouldShowBondingCurve) {
        return (
          <MintComponentLayout release={release} isInModal={isInModal}>
            <BondingCurve
              data={release}
              collectButtonSource={collectButtonSource}
              isInModal={isInModal}
            />
          </MintComponentLayout>
        );
      }

      return (
        <MintComponentLayout release={release} isInModal={isInModal}>
          <EditionMintComponent
            releaseInfo={release}
            collectButtonSource={collectButtonSource}
            isInModal={isInModal}
          />
          {!isInModal && <SocialProofBar releaseId={release.id} totalMinted={totalMinted} />}
        </MintComponentLayout>
      );
    }
    case 'TIERED_EDITION': {
      return (
        <MintComponentLayout release={release} isInModal={isInModal}>
          <TiersMintComponent
            release={release}
            collectButtonSource={collectButtonSource}
            isInModal={isInModal}
            TEMPORARY_showCustomPrice={TEMPORARY_showCustomPrice}
          />
          {!isInModal && <SocialProofBar releaseId={release.id} totalMinted={totalMinted} />}
        </MintComponentLayout>
      );
    }

    default:
      exhaustiveCheck(release.auctionContractType);
      return null;
  }
}

function MintComponentLayout({
  release,
  children,
  isInModal,
}: {
  release: FragmentType<MintComponentSongInfoFragmentDoc>;
  children: React.ReactNode;
  isInModal: boolean;
}) {
  return (
    <div className="mx-auto mt-3 flex w-full lt:mt-8 lt:max-w-sm">
      <div className="flex w-full flex-col rounded-md lt:max-w-sm">
        <div
          className={twMerge(
            'w-full overflow-hidden rounded-t-lg bg-white',
            isInModal ? 'flex border-b border-base200 pb-4' : 'hidden p-4 shadow-tertiary lt:flex',
          )}
        >
          <SongInfo release={release} />
        </div>
        {children}
      </div>
    </div>
  );
}
